import useClosePositionUtils from 'src/hooks/positions/useClosePositionUtils';
import useClosePositionModalHandlers from 'src/components/Modals/ClosePositionModal/hooks/useClosePositionModalHandlers';
import useClosePositionModalState from 'src/components/Modals/ClosePositionModal/hooks/useClosePositionModalState';
import { UIOrderType } from '@velocity-exchange/common';
import useModal from 'src/components/Modals/hooks/useModal';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';

interface Params {
	marketIndex: number;
	uiOrderType: UIOrderType;
}

export default function useClosePositionModal({
	marketIndex,
	uiOrderType,
}: Params) {
	const { hide } = useModal();
	const isMobileScreenSize = useIsMobileScreenSize();

	const {
		closePosition,
		isClosingPosition,
		slippage,
		showAccountValues,
		currentSettings: { showClosePositionConfirmation },
		setCurrentSettings,
		priceInputValue,
		setPriceInputValue,
		baseSizeInputValue,
		setBaseSizeInputValue,
		baseSizeInputValueBigNum,
		openPositionSizeBigNum,
		maxBaseSizeString,
		baseAssetSymbol,
		markPriceToUse,
		position,
		stepSize,
		sizeDigits,
		autoCalculatedQuoteSize,
		errorMessageInfo,
		priceImpactInfo,
	} = useClosePositionUtils({
		marketIndex,
		uiOrderType,
		onClosePositionSuccess: hide,
	});

	const {
		setQuoteSizeInputValue,
		setIsQuoteSizeFromQuoteInput,
		quoteSizeInputValueToUse,
		openPositionSizeDisplay,
		positionSliderValue,
	} = useClosePositionModalState({
		position,
		openPositionSizeBigNum,
		baseSizeInputValueBigNum,
		markPriceToUse,
		autoCalculatedQuoteSize,
		showAccountValues,
	});

	const isMarketOrder = uiOrderType === 'market';

	const {
		handlePriceInputChange,
		handleSizeInputChange,
		handlePositionSliderChange,
		handleToggleShowClosePositionConfirmation,
	} = useClosePositionModalHandlers({
		priceInputValue,
		baseSizeInputValueBigNum,
		setPriceInputValue,
		setBaseSizeInputValue,
		setQuoteSizeInputValue,
		setIsQuoteSizeFromQuoteInput,
		isMarketOrder,
		markPriceToUse,
		maxBaseSizeString,
		sizeDigits,
		openPositionSizeBigNum,
		setCurrentSettings,
		showClosePositionConfirmation,
	});

	const shouldShowError = errorMessageInfo?.messageType === 'error';

	const errorMessage = shouldShowError ? errorMessageInfo.message : undefined;

	const isSubmitButtonDisabled =
		!position ||
		isClosingPosition ||
		baseSizeInputValueBigNum.eqZero() ||
		(!isMarketOrder && !priceInputValue) ||
		shouldShowError ||
		(isMarketOrder && !priceImpactInfo);

	return {
		slippage,
		showClosePositionConfirmation,
		priceInputValue,
		baseSizeInputValue,
		baseAssetSymbol,
		stepSize,
		errorMessage,
		shouldShowError,
		handleDismiss: hide,
		closePosition,
		handlePriceInputChange,
		handleSizeInputChange,
		handlePositionSliderChange,
		handleToggleShowClosePositionConfirmation,
		quoteSizeInputValueToUse,
		openPositionSizeDisplay,
		positionSliderValue,
		isSubmitButtonDisabled,
		isSubmitting: isClosingPosition,
		isMarketOrder,
		isMobileScreenSize,
	};
}
